document.addEventListener('alpine:init', () => {
  // syncTargetId: where to mirror highlighted HTML. A page-level instance must
  // target the modal container; the default is its own subtree, and replacing
  // that kills its handlers.
  Alpine.data(
    'highlightableArea',
    (passageId, userId, excerptId, syncTargetId = null) => ({
      showColorMenu: false,
      selectedText: '',
      range: null,
      menuX: 0,
      menuY: 0,
      colors: [
        { name: 'yellow', class: 'bg-yellow-200' },
        { name: 'green', class: 'bg-green-200' },
        { name: 'blue', class: 'bg-blue-200' },
        { name: 'purple', class: 'bg-purple-200' },
        {
          name: 'clear',
          class: 'bg-white border border-gray-300 clear-highlight',
        },
      ],

      uid() {
        return Date.now().toString(36) + Math.random().toString(36).substring(2)
      },

      getAllNodesInRange(range) {
        // Initialize arrays to store full and partial nodes
        const nodes = []
        const fragments = []

        // If the range is collapsed, return empty arrays
        if (range.collapsed) {
          return { nodes, fragments }
        }

        // Handle the case where the range is within a single text node
        if (
          range.startContainer === range.endContainer &&
          range.startContainer.nodeType === Node.TEXT_NODE
        ) {
          fragments.push({
            node: range.startContainer,
            text: range.startContainer.textContent.substring(
              range.startOffset,
              range.endOffset
            ),
            startOffset: range.startOffset,
            endOffset: range.endOffset,
          })
          return { nodes, fragments }
        }

        // Create a TreeWalker to iterate through all nodes in the range
        const walker = document.createTreeWalker(
          range.commonAncestorContainer,
          NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
          {
            acceptNode: (node) => {
              if (
                node.nodeType === Node.TEXT_NODE &&
                node.textContent.trim() === ''
              ) {
                return NodeFilter.FILTER_REJECT
              }
              return NodeFilter.FILTER_ACCEPT
            },
          }
        )

        // Get the start and end points of the range
        const start = {
          node: range.startContainer,
          offset: range.startOffset,
        }
        const end = {
          node: range.endContainer,
          offset: range.endOffset,
        }

        let currentNode
        while ((currentNode = walker.nextNode())) {
          if (!range.intersectsNode(currentNode)) {
            continue
          }

          if (currentNode.nodeType === Node.TEXT_NODE) {
            if (this.isNodeInRange(currentNode, range)) {
              nodes.push(currentNode)
            } else if (this.isNodePartiallyInRange(currentNode, range)) {
              let text = currentNode.textContent
              let startOffset = currentNode === start.node ? start.offset : 0
              let endOffset =
                currentNode === end.node ? end.offset : text.length
              fragments.push({
                node: currentNode,
                text: text.substring(startOffset, endOffset),
                startOffset,
                endOffset,
              })
            }
          } else if (currentNode.nodeType === Node.ELEMENT_NODE) {
            if (this.isNodeInRange(currentNode, range)) {
              nodes.push(currentNode)
            }
          }
        }

        return { nodes, fragments }
      },

      isNodeInRange(node, range) {
        const nodeRange = document.createRange()
        nodeRange.selectNode(node)
        return (
          range.compareBoundaryPoints(Range.START_TO_START, nodeRange) <= 0 &&
          range.compareBoundaryPoints(Range.END_TO_END, nodeRange) >= 0
        )
      },

      isNodePartiallyInRange(node, range) {
        const nodeRange = document.createRange()
        nodeRange.selectNode(node)
        return (
          (range.compareBoundaryPoints(Range.START_TO_START, nodeRange) > 0 ||
            range.compareBoundaryPoints(Range.END_TO_END, nodeRange) < 0) &&
          range.intersectsNode(node)
        )
      },

      wrapNodesWithHighlight(range, color, uniqueId) {
        const { nodes, fragments } = this.getAllNodesInRange(range)

        // Function to create a highlight span
        const createHighlightSpan = (text) => {
          const span = document.createElement('span')
          span.className = `highlight ${color.class}`
          span.dataset.highlightId = uniqueId
          span.textContent = text
          return span
        }

        // Handle full nodes
        nodes.forEach((node) => {
          if (node.nodeType === Node.TEXT_NODE) {
            const span = createHighlightSpan(node.textContent)
            node.parentNode.replaceChild(span, node)
          }
        })

        // Handle fragments
        fragments.forEach(({ node, text, startOffset, endOffset }) => {
          // Create a range for just this fragment
          const fragmentRange = document.createRange()
          fragmentRange.setStart(node, startOffset)
          fragmentRange.setEnd(node, endOffset)

          // Extract the text we want to highlight
          const highlightedContent = fragmentRange.extractContents()
          const span = createHighlightSpan(highlightedContent.textContent)

          // Insert the highlighted span at the correct position
          fragmentRange.insertNode(span)
        })

        // Return the modified content within the original range
        return range.cloneContents()
      },

      getSelectedText() {
        const selection = window.getSelection()
        if (!selection || selection.rangeCount === 0) {
          this.showColorMenu = false
          return
        }

        const range = selection.getRangeAt(0)
        const text = range.toString()

        if (text.length > 0) {
          this.range = range
          this.selectedText = text

          const rect = range.getBoundingClientRect()
          const containerRect = this.$refs.container.getBoundingClientRect()

          this.menuX = rect.left - containerRect.left
          this.menuY = rect.bottom - containerRect.top + 5
          this.showColorMenu = true
        } else {
          this.showColorMenu = false
        }
      },

      clearHighlightsInRange(range) {
        // Get all highlight spans that intersect with the range
        const highlightSpans = []
        const iterator = document.createNodeIterator(
          range.commonAncestorContainer,
          NodeFilter.SHOW_ELEMENT,
          {
            acceptNode: (node) => {
              if (
                node.classList?.contains('highlight') &&
                range.intersectsNode(node)
              ) {
                return NodeFilter.FILTER_ACCEPT
              }
              return NodeFilter.FILTER_REJECT
            },
          }
        )

        let currentNode
        while ((currentNode = iterator.nextNode())) {
          highlightSpans.push(currentNode)
        }

        // Unwrap each highlight span, preserving its contents
        highlightSpans.forEach((span) => {
          const parent = span.parentNode
          while (span.firstChild) {
            parent.insertBefore(span.firstChild, span)
          }
          parent.removeChild(span)
        })
      },

      updateOtherPassages(text) {
        let container = document.getElementById(
          syncTargetId || 'excerpt_content_' + excerptId
        )
        if (container) container.innerHTML = text
      },

      createHighlight(color) {
        if (!this.range) return

        if (color.name === 'clear') {
          this.clearHighlightsInRange(this.range)

          fetch('/api/delete-highlight/', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': document.querySelector(
                '[name=csrfmiddlewaretoken]'
              ).value,
            },
            body: JSON.stringify({
              passageId: passageId,
              userId: userId,
              uniqueId: null,
              text: this.$refs.container.innerHTML,
            }),
          }).catch((error) => console.error('Error:', error))
        } else {
          const uniqueId = this.uid()
          this.wrapNodesWithHighlight(this.range, color, uniqueId)
          this.saveHighlight(color.name, uniqueId)
        }
        this.updateOtherPassages(this.$refs.container.innerHTML)
        this.showColorMenu = false
      },

      saveHighlight(colorName, uniqueId) {
        const data = {
          passageId: passageId,
          userId: userId,
          selectedText: this.selectedText.trim(),
          text: this.$refs.container.innerHTML,
          startIndex: this.range.startOffset,
          endIndex: this.range.endOffset,
          color: colorName,
          uniqueId: uniqueId,
        }

        fetch('/api/save-highlight/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': document.querySelector('[name=csrfmiddlewaretoken]')
              .value,
          },
          body: JSON.stringify(data),
        }).catch((error) => console.error('Error:', error))
      },
    })
  )
})
